/* eslint-disable react/forbid-prop-types */
import React, { useState, useEffect } from 'react';

import PropTypes from 'prop-types';
import { Button, ExpandCollapse, Typography } from '@telus-uds/components-web';

import { Box } from '../design/css';
import MultiSelect from './multiSelect';
import DateFilter from './dateFilters';
import request from '../utils/request';

import './styles.css';

const Filters = ({
  data,
  updateFilters,
  onApplyFilters,
  onResetFilters,
  filterState,
  staticLabels,
  setPage
}) => {
  const [adminUsers, setAdminUsers] = useState([]); 

  const {
    assignee,
    channelId,
    dealership,
    province,
    createdBy,
    status,
    flag,
    odysseyOrder,
    customerSegment,
    vbpRequired,
    priceApproverEmail
  } = filterState;
  const {
    filterByChannel,
    filterByAssignee,
    filterBySalesRep,
    buttonApply,
    buttonReset,
    filtersDropdownTitle,
    filterByStatus,
    statusFilters,
    messagesFlagFilters,
    filterByFlag,
    filterByOdysseyOrder,
    filterByDealership,
    filterByProvince,
    listOfProvinceFilters,
    filterByCustomerSegment,
    listOfCustomerSegments,
    filterByCustomPricing,
    listOfCustomePricingOptions,
    listOfOdysseyOrderOptions
  } = staticLabels;

  const { salesRepNames, channels, assignees, dealerships } = data;

  const channelList =
    channels &&
    channels.map(channel => ({ label: channel.text, value: channel.value }));

  const dealershipList =
    dealerships &&
    dealerships.map(row => ({
      label: row.text,
      value: row.value
    }));

    const getAdminUsers = async () => {
      try {
        const response = await request.post('/users/filter', { adminOnly: true });
        return Array.isArray(response.data.users) ? response.data.users : []; // Access the users array
      } catch (error) {
        return [];
      }
    };
  
    useEffect(() => {
      const fetchAdminUsers = async () => {
        const users = await getAdminUsers();
        setAdminUsers(users);
      };
      fetchAdminUsers();
    }, []);

  const onClickResetFilter = () => {
    onResetFilters();
    onApplyFilters(true);
  };

  const multiSelectFilters = [
    {
      label: filterByAssignee,
      name: 'assignee',
      options: assignees.map(({ label, id }) => ({ label, value: id })),
      value: assignee
    },

    {
      label: filterBySalesRep,
      name: 'createdBy',
      options: salesRepNames.map(({ label, email }) => ({
        label,
        value: email
      })),
      value: createdBy
    },
    {
      label: filterByStatus,
      name: 'status',
      options: statusFilters.map(({ text, value }) => ({ label: text, value })),
      value: status
    },
    {
      label: filterByFlag,
      name: 'flag',
      options: messagesFlagFilters.map(({ text, value }) => ({
        label: text,
        value
      })),
      value: flag
    },
    {
      label: filterByOdysseyOrder,
      name: 'odysseyOrder',
      options: listOfOdysseyOrderOptions,
      value: odysseyOrder
    },
    {
      label: filterByProvince,
      name: 'province',
      options: listOfProvinceFilters,
      value: province
    },
    {
      label: filterByChannel,
      name: 'channelId',
      options: channelList,
      value: channelId
    },
    {
      label: filterByDealership,
      name: 'dealership',
      options: dealershipList,
      value: dealership
    },
    {
      label: filterByCustomerSegment,
      name: 'customerSegment',
      options: listOfCustomerSegments,
      value: customerSegment
    },
    {
      label: filterByCustomPricing,
      name: 'vbpRequired',
      options: listOfCustomePricingOptions,
      value: vbpRequired
    },
    {
      label: 'Filter By Custom Pricing Approver',
      name: 'priceApproverEmail',
      options: Array.isArray(adminUsers) ? adminUsers.map(({ first_name, last_name, email }) => ({
        label: first_name + " " + last_name,
        value: email
      })) : [],
      value: priceApproverEmail
    }
  ];

  const getMultiSelectFilters = listOfFilters => {
    return listOfFilters.map(({ label, options, name, value }) => {
      return (
        <div key={name}>
          <MultiSelect
            updateFilters={updateFilters}
            label={label}
            options={options}
            name={name}
            value={value}
          />
        </div>
      );
    });
  };
  return (
    <ExpandCollapse initialOpen={[]} topDivider={false}>
      {(expandProps) => (
        <ExpandCollapse.Panel
          {...expandProps}
          panelId="filters"
          control={<Typography heading="h3" variant={{ size: 'h3', bold: true }}>{filtersDropdownTitle}</Typography>}
        >
          <div className="filters-main">
            {getMultiSelectFilters(multiSelectFilters)}
            <DateFilter filterState={filterState} updateFilters={updateFilters} />
          </div>
          <Box
            display="flex"
            flexDirection="row"
            mt={24}
            mdJustifyContent="flex-start"
            mdWidth={100}
            mdPadding="16px 0"
          >
            <Button
              onPress={() => {
                setPage(1);
                onApplyFilters(false);
              }}
              variant={{ priority: 'low' }}
            >
              {buttonApply}
            </Button>
            <Box width="2"> </Box>
            <Button
              onPress={() => {
                setPage(1);
                onClickResetFilter();
              }}
            >
              {buttonReset}
            </Button>
          </Box>
        </ExpandCollapse.Panel>
      )}
    </ExpandCollapse>
  );
};

Filters.propTypes = {
  staticLabels: PropTypes.object.isRequired,
  onApplyFilters: PropTypes.func.isRequired,
  onResetFilters: PropTypes.func.isRequired,
  updateFilters: PropTypes.func.isRequired,
  filterState: PropTypes.shape({
    startDateOpenDate: PropTypes.string,
    startDateSubmissionDate: PropTypes.string,
    endDateOpenDate: PropTypes.string,
    endDateSubmissionDate: PropTypes.string,
    endDate: PropTypes.string,
    filterCriteria: PropTypes.string,
    assignee: PropTypes.arrayOf(PropTypes.string),
    channelId: PropTypes.arrayOf(PropTypes.number),
    dealership: PropTypes.arrayOf(PropTypes.string),
    createdBy: PropTypes.arrayOf(PropTypes.string),
    flag: PropTypes.arrayOf(PropTypes.string),
    odysseyOrder: PropTypes.arrayOf(PropTypes.string),
    status: PropTypes.arrayOf(PropTypes.string),
    province: PropTypes.arrayOf(PropTypes.string),
    customerSegment: PropTypes.arrayOf(PropTypes.string),
    vbpRequired: PropTypes.arrayOf(PropTypes.string),
    customApprover: PropTypes.arrayOf(PropTypes.string),
    priceApproverEmail: PropTypes.arrayOf(PropTypes.string)
  }).isRequired,
  data: PropTypes.shape({
    salesRepNames: PropTypes.string,
    channels: PropTypes.arrayOf(PropTypes.shape({})),
    assignees: PropTypes.arrayOf(PropTypes.shape({})),
    dealerships: PropTypes.arrayOf(PropTypes.shape({}))
  }).isRequired,
  setPage: PropTypes.func.isRequired
};

export default Filters;
